/**
 * <rz-slideshow data-transition="slide|fade" data-autoplay="5" data-pause-on-hover>
 * Slide transition uses a native scroll-snap track (swipe for free); fade stacks slides.
 * Autoplay stops for reduced motion, pauses on hover/focus and has a pause button (WCAG 2.2.2).
 */
if (!customElements.get('rz-slideshow')) {
  customElements.define(
    'rz-slideshow',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = [...this.querySelectorAll('.slideshow__slide')];
        this.dots = [...this.querySelectorAll('[data-slide-to]')];
        this.toggle = this.querySelector('[data-autoplay-toggle]');
        this.fade = this.dataset.transition === 'fade';
        this.index = 0;
        if (this.slides.length < 2) return;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.slideTo))));
        if (!this.fade) this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.go(this.index - 1);
          if (e.key === 'ArrowRight') this.go(this.index + 1);
        });

        // Theme editor: show the slide being edited and stop rotating.
        this.addEventListener('shopify:block:select', (e) => {
          this.stop();
          const i = this.slides.indexOf(e.target.closest('.slideshow__slide'));
          if (i >= 0) this.go(i, 'instant');
        });

        const seconds = Number(this.dataset.autoplay);
        const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (seconds > 0 && !reduced) {
          this.delay = seconds * 1000;
          this.toggle?.addEventListener('click', () => (this.timer ? this.stop(true) : this.start()));
          if (this.hasAttribute('data-pause-on-hover')) {
            this.addEventListener('mouseenter', () => this.pause());
            this.addEventListener('mouseleave', () => this.resume());
          }
          this.addEventListener('focusin', () => this.pause());
          this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && this.resume());
          document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));
          this.start();
        } else if (this.toggle) {
          this.toggle.hidden = true;
        }
        this.render();
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      go(i, behavior = 'smooth') {
        this.index = (i + this.slides.length) % this.slides.length;
        if (!this.fade) {
          const slide = this.slides[this.index];
          this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior });
        }
        this.render();
      }

      onScroll() {
        const i = Math.round(Math.abs(this.track.scrollLeft) / (this.track.clientWidth || 1));
        if (i !== this.index) {
          this.index = i;
          this.render();
        }
      }

      render() {
        this.slides.forEach((slide, i) => {
          const active = i === this.index;
          slide.classList.toggle('is-active', active);
          if (this.fade) slide.inert = !active;
        });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      }

      start() {
        clearInterval(this.timer);
        this.stoppedByUser = false;
        this.timer = setInterval(() => this.go(this.index + 1), this.delay);
        this.toggle?.setAttribute('aria-pressed', 'false');
        if (this.toggle) this.toggle.ariaLabel = this.toggle.dataset.labelPause;
        this.classList.add('is-playing');
      }

      stop(byUser = false) {
        clearInterval(this.timer);
        this.timer = null;
        if (byUser) this.stoppedByUser = true;
        this.toggle?.setAttribute('aria-pressed', 'true');
        if (this.toggle) this.toggle.ariaLabel = this.toggle.dataset.labelPlay;
        this.classList.remove('is-playing');
      }

      pause() {
        clearInterval(this.timer);
        this.paused = true;
      }

      resume() {
        if (!this.paused || this.stoppedByUser || !this.delay) return;
        this.paused = false;
        if (this.timer) this.start();
      }
    }
  );
}
